import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import Button from "../../../components/Button";
import Input from "../../../components/Input";
import InputCheckboxUnique from "../../../components/InputCheckboxUnique";
import InputSelect from "../../../components/InputSelect";
import Modal from "../../../components/Modal";
import Table from "../../../components/Table";
import { convertIntToMoney, convertMoenyToInt } from "../../../helpers/conversions";
import { renderError } from "../../../helpers/errors";
import { createOptions } from "../../../helpers/selects";
import api from "../../../services/api";
import { hasPermission } from "../../../services/auth";
import { toast } from "react-toastify";

const OrderCommissionsTab = ({ order, refreshOrder }) => {
    const [admins, setAdmins] = useState([]);
    const [loadingAdmins, setLoadingAdmins] = useState(false);

    const [showModal, setShowModal] = useState(false);
    const [selectedAdmin, setSelectedAdmin] = useState(null);
    const [value, setValue] = useState('R$ 0,00');
    const [isPaid, setIsPaid] = useState(false);
    const [expectedPaymentAt, setExpectedPaymentAt] = useState('');
    const [paidAt, setPaidAt] = useState('');
    const [saving, setSaving] = useState(false);
    const [currentCommission, setCurrentCommission] = useState(null);

    const canEdit = hasPermission('edit_financial');

    useEffect(() => {
        fetchAdmins();
    }, []);

    useEffect(() => {
        if(!showModal){
            resetForm();
        }
    }, [showModal]);

    useEffect(() => {
        if(!isPaid){
            setPaidAt('');
        }
    }, [isPaid]);

    const fetchAdmins = () => {
        setLoadingAdmins(true);
        api.get(`/list`).then(res => {
            setAdmins(res.data.resources ?? []);
        }).catch(error => {
            renderError(error);
        }).then(() => setLoadingAdmins(false));
    }

    const commissions = useMemo(() => order?.commissions ?? [], [order]);

    const headers = useMemo(() => {
        const baseHeaders = ['Vendedor', 'Valor da comissão', 'Status', 'Previsão de pagamento', 'Data de pagamento'];
        if(canEdit){
            baseHeaders.push('Editar');
            baseHeaders.push('Excluir');
        }
        return baseHeaders;
    }, [canEdit]);

    const rows = useMemo(() => {
        return commissions.map(commission => ({
            data: [
                commission.admin_name || '-',
                `R$ ${convertIntToMoney(commission.value)}`,
                commission.is_paid ? 'Paga' : 'Pendente',
                commission.expected_payment_at ? moment(commission.expected_payment_at).format('DD/MM/YYYY') : '-',
                commission.paid_at ? moment(commission.paid_at).format('DD/MM/YYYY') : '-'
            ],
            buttons: canEdit ? [
                <Button
                    key={`edit-${commission.id}`}
                    type="secondary"
                    size="small"
                    text="Editar"
                    action={() => openModal(commission)}
                />,
                <Button
                    key={`delete-${commission.id}`}
                    type="secondary"
                    size="small"
                    text="Excluir"
                    action={() => handleDelete(commission)}
                />
            ] : undefined
        }));
    }, [commissions, canEdit]);

    const openModal = (commission = null) => {
        if(commission){
            setCurrentCommission(commission);
            setSelectedAdmin(commission.admin_id);
            setValue(`R$ ${convertIntToMoney(commission.value)}`);
            setIsPaid(commission.is_paid === true);
            setExpectedPaymentAt(commission.expected_payment_at ?? '');
            setPaidAt(commission.paid_at ?? '');
        }
        setShowModal(true);
    }

    const resetForm = () => {
        setSelectedAdmin(null);
        setValue('R$ 0,00');
        setIsPaid(false);
        setExpectedPaymentAt('');
        setPaidAt('');
        setSaving(false);
        setCurrentCommission(null);
    }

    const handleSave = () => {
        if(!order?.id){
            toast.error('Pedido não encontrado.');
            return;
        }

        if(!selectedAdmin){
            toast.error('Selecione um vendedor.');
            return;
        }

        const intValue = convertMoenyToInt(value);
        if(intValue <= 0){
            toast.error('Informe um valor maior que zero.');
            return;
        }

        const payload = {
            order_id: order.id,
            admin_id: selectedAdmin,
            value: intValue,
            is_paid: isPaid,
            expected_payment_at: expectedPaymentAt || null,
            paid_at: isPaid ? (paidAt || null) : null
        };

        setSaving(true);

        const request = currentCommission
            ? api.put(`/order/commission/${currentCommission.id}`, payload)
            : api.post(`/order/commission`, payload);

        request.then(() => {
            toast.success(currentCommission ? 'Comissão atualizada com sucesso.' : 'Comissão cadastrada com sucesso.');
            setShowModal(false);
            if(refreshOrder){
                refreshOrder();
            }
        }).catch(error => {
            renderError(error);
        }).then(() => setSaving(false));
    }

    const handleDelete = commission => {
        if(!window.confirm('Deseja realmente excluir esta comissão?')){
            return;
        }

        api.delete(`/order/commission/${commission.id}`).then(() => {
            toast.success('Comissão excluída com sucesso.');
            if(refreshOrder){
                refreshOrder();
            }
        }).catch(error => {
            renderError(error);
        });
    }

    return (
        <>
            <Modal
                show={showModal}
                close={() => setShowModal(false)}
                title={`${currentCommission ? 'Editar' : 'Cadastrar'} comissão`}
                footer={
                    <>
                        <Button
                            type="primary"
                            size="small"
                            text="Salvar"
                            svg="save-white"
                            action={handleSave}
                            loading={saving}
                        />
                    </>
                }
            >
                <div className="section">
                    <InputSelect
                        label="Vendedor"
                        options={createOptions(admins, 'name', 'id')}
                        value={selectedAdmin}
                        change={setSelectedAdmin}
                        loading={loadingAdmins}
                    />
                    <Input
                        label="Valor da comissão"
                        value={value}
                        change={setValue}
                        mask="convertToMoney"
                    />
                    <Input
                        label="Previsão de pagamento"
                        type="date"
                        value={expectedPaymentAt ?? ''}
                        change={setExpectedPaymentAt}
                    />
                    <InputCheckboxUnique
                        label="Paga"
                        checked={isPaid}
                        change={setIsPaid}
                    />
                    <Input
                        label="Data de pagamento"
                        type="date"
                        value={paidAt ?? ''}
                        change={setPaidAt}
                        disabled={!isPaid}
                    />
                </div>
            </Modal>

            {canEdit &&
                <div className="row">
                    <div className="col">
                        <Button
                            type="secondary"
                            size="small"
                            text="Cadastrar comissão"
                            action={() => openModal()}
                        />
                        <br />
                    </div>
                </div>
            }

            <Table
                header={headers}
                rows={rows}
                loading={false}
            />
        </>
    );
}

export default OrderCommissionsTab;
